import clsx from 'clsx'
import { Portal } from 'components/utility/portal'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

const DrawerContext = createContext()

const DrawerTrigger = ({ children, className, onTrigger }) => {
  const { handleOpen } = useContext(DrawerContext)

  const handleClick = () => {
    handleOpen()
    onTrigger?.()
  }

  return (
    <div onClick={handleClick} className={className}>
      {children}
    </div>
  )
}

const DrawerClose = ({ children, onClose, className }) => {
  const { handleClose } = useContext(DrawerContext)
  return (
    <div onClick={handleClose} className={className}>
      {children}
    </div>
  )
}

const DrawerContent = ({ children, className, overlayClassName, wrapperClassName, direction = 'right' }) => {
  const { expand, duration, timing, handleClose } = useContext(DrawerContext)

  const styles = useMemo(
    () => ({
      transition: `all ${duration}ms ${timing}`,
    }),
    [duration, timing]
  )

  return (
    <Portal>
      <div className={clsx('drawer', className)}>
        <div
          style={styles}
          onClick={handleClose}
          aria-expanded={expand}
          className={clsx('drawer-overlay', overlayClassName)}
        ></div>
        <div aria-expanded={expand} style={styles} className={clsx('drawer-container', direction, wrapperClassName)}>
          {children}
        </div>
      </div>
    </Portal>
  )
}

export default function Drawer({ children, show = false, duration = 600, timing = 'ease-in-out', onOpen, onClose }) {
  const [open, setOpen] = useState(false)
  const [expand, setExpand] = useState(false)

  useEffect(() => {
    if (show === open) return
    if (show) {
      setOpen(true)
      setTimeout(() => setExpand(true), 100)
    } else {
      setExpand(false)
      setTimeout(() => setOpen(false), duration)
    }
  }, [show, open, duration])

  useEffect(() => {
    document.body.classList.toggle('overlay-activated', open)
    return () => {
      document.body.classList.remove('overlay-activated')
    }
  }, [open])

  if (!open) return null

  return (
    <DrawerContext.Provider value={{ expand, duration, timing, handleOpen: onOpen, handleClose: onClose }}>
      {children}
    </DrawerContext.Provider>
  )
}

Drawer.Close = DrawerClose
Drawer.Trigger = DrawerTrigger
Drawer.Content = DrawerContent
